import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, Button, Icon, StatusPill, SearchInput, FilterSelect, Pagination, DataTable } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { VENDOR_STATUS_PILL, vendorPill } from '../data/messageStatus';
import { campaignService } from '../../../../services/apiService';
import { formatShortDate } from '../data/format';

/**
 * Vendor Audience — segments the REAL vendor base via GET /campaigns/audience.
 * Filtering, search and paging are all server-side (the endpoint also returns the facet
 * options + counts for the current query), so there is no client-side table control here.
 * "Send Regularization Links" hands off to the Regularization screen, where the live bulk
 * send lives.
 */
const EMPTY_FILTERS = { status: 'All', region: 'All', country: 'All', currency: 'All', msme: 'All', category: 'All', type: 'All' };
const PAGE_SIZE = 9;
const SEARCH_DEBOUNCE_MS = 300;
const MSME_LABEL = { MSME: 'MSME', NON_MSME: 'Non-MSME', PENDING: 'Pending' };

// key = query param + filters-state key; optionsKey = key inside aud.data.options
const FILTER_FIELDS = [
  { key: 'status', label: 'Vendor Status', selectLabel: 'Status', icon: 'verified_user', optionsKey: 'statuses' },
  { key: 'region', label: 'Region (India)', selectLabel: 'Region', icon: 'map', optionsKey: 'regions' },
  { key: 'country', label: 'Country', selectLabel: 'Country', icon: 'public', optionsKey: 'countries' },
  { key: 'currency', label: 'Currency', selectLabel: 'Currency', icon: 'payments', optionsKey: 'currencies' },
  { key: 'msme', label: 'MSME', selectLabel: 'MSME', icon: 'storefront', optionsKey: 'msme' },
  { key: 'category', label: 'Category', selectLabel: 'Category', icon: 'category', optionsKey: 'categories' },
  { key: 'type', label: 'Vendor Type', selectLabel: 'Type', icon: 'factory', optionsKey: 'types' },
];

/** Display label for a raw option value; the raw value is what goes on the wire. */
function humanise(key, value) {
  if (value === 'All') return 'All';
  if (key === 'msme') return MSME_LABEL[value] || value;
  if (key === 'status') return VENDOR_STATUS_PILL[value]?.label || value;
  return value;
}

export function AudiencePage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce typing → one request per pause; a new search always starts from page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const params = useMemo(() => {
    const p = { page, limit: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== 'All') p[k] = v;
    });
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [filters, debouncedSearch, page]);

  const aud = useApiData(() => campaignService.audience(params), [params], { cacheKey: `campaign:audience:${JSON.stringify(params)}` });

  const goToRegularization = useCallback(() => navigate('/campaigning-tool/regularization'), [navigate]);

  usePageChrome(
    () => ({
      title: 'Vendor Audience',
      subtitle: 'Segment the real vendor base for targeted outreach',
      actions: (
        <Button variant="primary" icon="forward_to_inbox" onClick={goToRegularization}>
          Send Regularization Links
        </Button>
      ),
    }),
    [goToRegularization]
  );

  const data = aud.data;
  const matched = data?.matched ?? 0;
  const total = data?.total ?? 0;
  const share = total > 0 ? (matched / total) * 100 : 0;
  const activeCount = Object.values(filters).filter((v) => v !== 'All').length;
  const options = data?.options || {};
  const vendors = data?.data || [];
  const meta = data?.meta || { page, limit: PAGE_SIZE, total: vendors.length };

  const initialLoading = aud.loading && !data;
  const hardError = aud.error && !data;

  /** Facet values for a filter, always including the currently selected value so the <select> never blanks. */
  const optionValues = (field) => {
    const values = (options[field.optionsKey] || [])
      .map((o) => o.value)
      .filter((v) => v !== null && v !== undefined && v !== '');
    const current = filters[field.key];
    if (current !== 'All' && !values.includes(current)) values.unshift(current);
    return values;
  };

  const columns = [
    {
      key: 'company_name',
      header: 'Vendor',
      render: (v) => (
        <div className="min-w-0">
          <div className="font-semibold text-cos-ink">{v.company_name}</div>
          <div className="mono text-2xs text-cos-ink-muted">{v.vendor_code || 'No vendor code'}</div>
        </div>
      ),
    },
    { key: 'supplier_category', header: 'Category', render: (v) => <span className="text-cos-ink-secondary">{v.supplier_category || '—'}</span> },
    { key: 'vendor_type', header: 'Type', render: (v) => <span className="text-cos-ink-secondary">{v.vendor_type || '—'}</span> },
    { key: 'country_origin', header: 'Country', render: (v) => <span className="text-cos-ink-secondary">{v.country_origin || '—'}</span> },
    { key: 'region', header: 'Region', render: (v) => <span className="text-cos-ink-secondary">{v.region || '—'}</span> },
    { key: 'currency', header: 'Currency', render: (v) => <span className="mono text-cos-ink-secondary">{v.currency || '—'}</span> },
    {
      key: 'msme_status',
      header: 'MSME',
      render: (v) => <span className="text-cos-ink-secondary">{v.msme_status ? MSME_LABEL[v.msme_status] || v.msme_status : '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (v) => {
        const p = vendorPill(v.status);
        return <StatusPill status={p.pill} label={p.label} />;
      },
    },
    {
      key: 'created_at',
      header: 'Onboarded',
      align: 'right',
      render: (v) => (
        <span className="mono text-cos-ink-secondary">{v.created_at ? formatShortDate(String(v.created_at).slice(0, 10)) : '—'}</span>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      {/* Filters + match summary */}
      <div className="space-y-5 lg:col-span-4 xl:col-span-3">
        <GlassCard accentRail className="relative">
          <div className="eyebrow text-cos-teal-deep/80">Matching vendors</div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="mono text-[42px] font-bold leading-none tracking-tight text-cos-teal-deep">
              {data ? matched.toLocaleString('en-US') : '—'}
            </span>
          </div>
          <div className="mono mt-1.5 text-2xs text-cos-ink-muted">of {total.toLocaleString('en-US')} total vendors</div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[rgba(70,80,72,0.1)]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cos-teal-bright to-cos-teal-deep transition-[width] duration-500"
              style={{ width: `${total > 0 ? Math.max(2, share) : 0}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-2xs text-cos-ink-muted">
            <span>{share.toFixed(1)}% of base</span>
            <span>{activeCount} filter{activeCount === 1 ? '' : 's'} active</span>
          </div>
        </GlassCard>

        <GlassCard padded={false}>
          <div className="flex items-center justify-between border-b border-cos-line-subtle px-4 py-3">
            <span className="flex items-center gap-2 text-[13px] font-bold text-cos-ink">
              <Icon name="filter_alt" className="text-[18px] text-cos-teal-deep" /> Segment filters
            </span>
            {activeCount > 0 && (
              <button onClick={clearFilters} className="text-2xs font-semibold text-cos-teal-deep hover:underline">
                Clear all
              </button>
            )}
          </div>
          <div className="space-y-3 p-4">
            {FILTER_FIELDS.map((f) => {
              const values = optionValues(f);
              const labels = ['All', ...values.map((v) => humanise(f.key, v))];
              const labelToValue = new Map(values.map((v) => [humanise(f.key, v), v]));
              return (
                <FilterField key={f.key} label={f.label}>
                  <FilterSelect
                    label={f.selectLabel}
                    value={humanise(f.key, filters[f.key])}
                    options={labels}
                    onChange={(label) => setFilter(f.key, label === 'All' ? 'All' : labelToValue.get(label) ?? label)}
                    icon={f.icon}
                  />
                </FilterField>
              );
            })}
          </div>
        </GlassCard>
      </div>

      {/* Vendor table */}
      <div className="lg:col-span-8 xl:col-span-9">
        <GlassCard padded={false} className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-cos-line-subtle px-5 py-4">
            <div>
              <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">Vendor list</h3>
              <p className="text-2xs text-cos-ink-muted">
                {aud.loading && data ? 'Refreshing…' : 'Live vendor records matching the current segment'}
              </p>
            </div>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by company, vendor code, or email…" className="w-full max-w-xs" />
          </div>

          {aud.error && data && (
            <div className="flex items-center gap-2 border-b border-cos-line-subtle bg-cos-status-amber-bg px-5 py-2 text-2xs text-cos-status-amber">
              <Icon name="warning" className="text-[15px]" />
              <span>Refresh failed — showing the last loaded results. {aud.error}</span>
            </div>
          )}

          <div className="px-3 py-1">
            {hardError ? (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <Icon name="cloud_off" className="text-[32px] text-cos-status-coral" />
                <div>
                  <div className="text-[15px] font-bold text-cos-ink">Couldn&apos;t load the vendor audience</div>
                  <p className="mt-1 text-[13px] text-cos-ink-secondary">{aud.error}</p>
                </div>
                <Button variant="secondary" icon="refresh" onClick={aud.refetch}>
                  Retry
                </Button>
              </div>
            ) : initialLoading ? (
              <div className="animate-pulse space-y-3 px-3 py-6">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-[rgba(70,80,72,0.08)]" />
                ))}
              </div>
            ) : (
              <DataTable columns={columns} rows={vendors} rowKey={(v) => v.id} emptyLabel="No vendors match this segment." />
            )}
          </div>

          {!hardError && (
            <div className="border-t border-cos-line-subtle px-5 py-3">
              <Pagination page={meta.page} pageSize={meta.limit} total={meta.total} onPageChange={setPage} noun="vendors" />
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-cos-ink-muted">{label}</div>
      {children}
    </div>
  );
}

export default AudiencePage;
